import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { debounce } from "lodash";
import { forkJoin } from "rxjs";
import { finalize } from "rxjs/operators";
import { environment as appEnv } from "../../../environments/environment";
import { NotifierPropertiesService } from "../../models/notifier-properties.service";
import { QueuesService } from "../../models/queues.service";
import { OutdatedNotifierService } from "../../outdated-notifier/outdated-notifier.service";
import { RoundTimeService } from "../../round-time/round-time.service";

@Component({
  selector: "ab-queue-list",
  templateUrl: "./queue-list.component.html",
})
export class QueueListComponent implements OnChanges {
  @Input() public dur: string;
  @Input() public timeDurAgo: string;
  @Input() public project: any;
  @Input() public queuePage: number;
  @Input() public sortColumn: string;
  @Input() public sortDesc: boolean;
  @Input() public search: string;
  @Input() public environment: string;

  public queues: any[];
  public totalPages: number;
  public totalQueues: number;
  public loading: boolean;
  public appEnv = appEnv;
  public debounceSearchQueue = debounce(() => {
    this.searchQueue();
  }, 1500);
  public needsNotifierUpgrade: boolean;
  public notifierName: string;

  private PAGE_SIZE = 20;
  private SUPPORTED_NOTIFIERS = [
    "airbrake-ruby",
    "gobrake",
    "pybrake",
    "airbrake-js/browser",
    "airbrake-js/node",
  ];

  constructor(
    private queuesService: QueuesService,
    private roundTimeService: RoundTimeService,
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private outdatedNotifierService: OutdatedNotifierService,
    private notifierPropertiesService: NotifierPropertiesService,
  ) {}

  public ngOnChanges(_changes: SimpleChanges) {
    this.loading = true;

    forkJoin([this.fetchQueues(), this.fetchNotifierProps()])
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe(([queuesData, notifierPropsData]: any[]) => {
        this.queues = queuesData.queues;
        this.totalQueues = queuesData.count;
        this.totalPages = Math.ceil(this.totalQueues / this.PAGE_SIZE);

        this.notifierName = notifierPropsData.notifier_name;
        const name = notifierPropsData.notifier_name;
        const version = notifierPropsData.notifier_version;
        if (this.outdatedNotifierService.isOutdatedForAPM(name, version)) {
          this.needsNotifierUpgrade = true;
        }
      });
  }

  public trackBy(_index: number, timing: any) {
    return timing.queueId;
  }

  public sortBy(column: string) {
    let sortDesc;
    if (column === this.sortColumn) {
      sortDesc = !this.sortDesc;
    } else {
      sortDesc = column !== "queue";
    }

    this.$state.go(this.$uiRouterGlobals.current.name, {
      sortQueueColumn: column,
      sortQueueDesc: sortDesc,
      queuePage: 1,
    });
  }

  public searchQueue() {
    this.$state.go(this.$uiRouterGlobals.current.name, {
      queuePage: 1,
      queueSearch: this.search,
    });
  }

  public shouldShowCTA(): boolean {
    if (this.needsNotifierUpgrade && this.queues && this.queues.length === 0) {
      return true;
    } else {
      return false;
    }
  }

  public isSupportedNotifier() {
    if (this.queues && this.queues.length === 0) {
      return this.SUPPORTED_NOTIFIERS.indexOf(this.notifierName) > -1;
    }

    return true;
  }

  private fetchQueues() {
    const params = {
      projectId: this.project.id,
      time__gte: this.roundTimeService.timestampFor(this.timeDurAgo, this.dur),
      limit: this.PAGE_SIZE,
      page: this.queuePage,
      order: this.sortColumn,
    } as any;
    if (this.sortDesc) {
      params.order += " desc";
    }
    if (this.search) {
      params.queue__ieq = this.search;
    }
    if (this.environment) {
      params.environment = this.environment;
    }

    return this.queuesService.query(params);
  }

  private fetchNotifierProps() {
    this.needsNotifierUpgrade = false;
    const params = { projectId: this.project.id };

    return this.notifierPropertiesService.get(params);
  }
}
